import * as React from 'react';

import { Celula } from '@/componentes/celula';
import { Carregando, Falha, Vazio } from '@/componentes/estado-da-tela';
import { Segmentado } from '@/componentes/segmentado';
import { Valor } from '@/componentes/valor';
import { mensagemDoErro, useAcessosAdmin } from '@/dados';

/* A curva de visitas.
 *
 * Barras em CSS, sem biblioteca de gráfico: são duas séries e um eixo de
 * tempo, e trazer uma dependência de desenho para isso custaria mais que o que
 * ela resolveria.
 *
 * Dia sem visita NÃO vem do banco (o agrupamento só devolve dias com linha), e
 * é preenchido aqui: uma curva que pula os dias vazios encosta as semanas umas
 * nas outras e faz um fim de semana parado parecer movimento. */

const PERIODOS = [
  { valor: 30, rotulo: '30 dias' },
  { valor: 90, rotulo: '90 dias' },
  { valor: 365, rotulo: '1 ano' },
];

type Barra = { dia: string; visitas: number; pessoas: number };

/** Completa os dias sem visita, do mais antigo ao de hoje. */
function preencherDias(serie: Barra[], dias: number): Barra[] {
  const porDia = new Map(serie.map((d) => [d.dia, d]));
  const hoje = new Date();
  const completa: Barra[] = [];
  for (let i = dias - 1; i >= 0; i -= 1) {
    const data = new Date(hoje);
    data.setDate(hoje.getDate() - i);
    const chave = data.toISOString().slice(0, 10);
    completa.push(porDia.get(chave) ?? { dia: chave, visitas: 0, pessoas: 0 });
  }
  return completa;
}

export function Acessos() {
  const [dias, setDias] = React.useState(30);
  const acessos = useAcessosAdmin(dias);

  const barras = React.useMemo(
    () => (acessos.data ? preencherDias(acessos.data.serie, dias) : []),
    [acessos.data, dias],
  );
  const teto = Math.max(1, ...barras.map((b) => b.visitas));
  const total = barras.reduce((soma, b) => soma + b.visitas, 0);

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-tinta-2 text-apoio" aria-live="polite">
          {acessos.isPending ? 'Carregando' : `${total} visitas no período`}
        </p>
        <Segmentado
          rotulo="Período"
          rotuloOculto
          tamanho="pequeno"
          valor={String(dias)}
          aoMudar={(v) => setDias(Number(v))}
          opcoes={PERIODOS.map((p) => ({ valor: String(p.valor), rotulo: p.rotulo }))}
        />
      </div>

      {acessos.isError ? (
        <Falha
          oQue="as visitas"
          motivo={mensagemDoErro(acessos.error)}
          aoTentarDeNovo={() => void acessos.refetch()}
        />
      ) : acessos.isPending ? (
        <Carregando linhas={1} rotulo="Carregando as visitas." />
      ) : total === 0 ? (
        <Vazio
          titulo="Nenhuma visita no período."
          ondePreencher="A contagem começou quando o painel entrou no ar; não há histórico anterior a isso."
        />
      ) : (
        <Celula respiro="folgado" className="grid gap-3">
          <div className="flex h-40 items-end gap-px" role="img" aria-label={`${total} visitas em ${dias} dias`}>
            {barras.map((barra) => (
              <div
                key={barra.dia}
                className="bg-mel min-h-px flex-1 rounded-t-sm"
                style={{ height: `${(barra.visitas / teto) * 100}%` }}
                title={`${barra.dia}: ${barra.visitas} visitas, ${barra.pessoas} pessoas`}
              />
            ))}
          </div>
          <div className="text-tinta-2 flex justify-between text-nota">
            <Valor valor={barras[0]?.dia} formato="data" />
            <span>Pico de {teto} visitas em um dia</span>
            <Valor valor={barras[barras.length - 1]?.dia} formato="data" />
          </div>
        </Celula>
      )}
    </div>
  );
}
